/** Shared account, result and stats shapes, plus the stats computation used by both sides. */
import { EVENTS, type EventId } from './events.js';
import { pointsFor } from './scoring.js';

export const NAME_MIN = 2;
export const NAME_MAX = 16;

export function sanitiseName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < NAME_MIN || name.length > NAME_MAX) return null;
  if (!/^[\p{L}\p{N} _.'-]+$/u.test(name)) return null;
  return name;
}

/** One completed event attempt set (a whole 100m, or all three long jumps). */
export interface CadenceSummary {
  avgCadence: number;
  peakCadence: number;
  presses: number;
  rejected: number;
  suspicious: boolean;
}

export interface ResultRow {
  /** Stable event identity for deduplicating browser history after reconnect/reload. */
  resultKey?: string;
  cadence?: CadenceSummary;
  playedAt: string;
  event: EventId;
  /** null = no mark (all fouls / three misses / DNF). */
  mark: number | null;
  points: number;
  mode: 'solo' | 'online';
}

export interface EventStats {
  event: EventId;
  count: number;
  best: number | null;
  bestPoints: number;
  avgPoints: number;
  /** Averages over the last 5 and the 5 before, for trend arrows. */
  recentPoints: number;
  earlierPoints: number;
}

export interface UserStats {
  name: string;
  results: ResultRow[];
  events: EventStats[];
  summary: {
    results: number;
    eventsTried: number;
    /** Sum of the best points in each event: your "dream decathlon". */
    bestTotal: number;
    /** Points in the most recent result of each event, summed. */
    currentTotal: number;
  };
}

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

export function computeStats(name: string, rows: ResultRow[]): UserStats {
  const sorted = [...rows].sort((a, b) => a.playedAt.localeCompare(b.playedAt));
  const events: EventStats[] = [];
  let bestTotal = 0;
  let currentTotal = 0;
  for (const ev of EVENTS) {
    const mine = sorted.filter((r) => r.event === ev.id);
    if (!mine.length) continue;
    const pts = mine.map((r) => r.points);
    const marks = mine.map((r) => r.mark).filter((m): m is number => m !== null);
    const best = marks.length
      ? ev.unit === 'seconds'
        ? Math.min(...marks)
        : Math.max(...marks)
      : null;
    const bestPoints = Math.max(0, ...pts);
    bestTotal += bestPoints;
    currentTotal += pts[pts.length - 1] ?? 0;
    events.push({
      event: ev.id,
      count: mine.length,
      best,
      bestPoints,
      avgPoints: Math.round(avg(pts)),
      recentPoints: Math.round(avg(pts.slice(-5))),
      earlierPoints: Math.round(avg(pts.slice(-10, -5))),
    });
  }
  return {
    name,
    results: sorted,
    events,
    summary: { results: sorted.length, eventsTried: events.length, bestTotal, currentTotal },
  };
}

/** Validate an incoming result from a client. */
export function cleanResult(raw: unknown, mode: ResultRow['mode']): ResultRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const ev = EVENTS.find((e) => e.id === r['event']);
  if (!ev) return null;
  const mark =
    r['mark'] === null
      ? null
      : typeof r['mark'] === 'number' && Number.isFinite(r['mark'])
        ? Math.round(r['mark'] * 100) / 100
        : undefined;
  if (mark === undefined) return null;
  // Sanity bounds: nothing better than the world record by a wide margin.
  if (mark !== null) {
    if (ev.unit === 'seconds' && (mark <= 0 || mark < ev.worldClass * 0.85)) return null;
    if (ev.unit === 'metres' && (mark < 0 || mark > ev.worldClass * 1.25)) return null;
  }
  const c = r['cadence'] as Record<string, unknown> | undefined;
  const cadence =
    c &&
    ['avgCadence', 'peakCadence', 'presses', 'rejected'].every(
      (k) =>
        typeof c[k] === 'number' &&
        Number.isFinite(c[k]) &&
        (c[k] as number) >= 0 &&
        (c[k] as number) <= 100000,
    ) &&
    typeof c['suspicious'] === 'boolean'
      ? (c as unknown as CadenceSummary)
      : undefined;
  return {
    playedAt: new Date().toISOString(),
    event: ev.id,
    mark,
    points: pointsFor(ev, mark),
    mode,
    ...(cadence ? { cadence } : {}),
  };
}
